/** The app's session: who is signed in and the redirects around sign-in. */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useApolloClient, useQuery } from '@apollo/client/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { navigation } from '@/navigation';
import { isPublicRoute } from '../constants';
import { ACCOUNT_ENTRY_ACKNOWLEDGED_KEY } from '../constants/account-entry';
import { forgetMasterAccountBoardUrl } from '../utils/master-account-board-return';
import { masterPermissionCheck } from '../utils/master-context';
import { masterAccountBoardHomePath } from '@/features/master-account-board-shell/utils/master-account-board-home';
import {
  forgetLastUser,
  forgetReturnUrl,
  rememberLastUser,
  rememberReturnUrl,
  takeReturnUrl
} from '../utils/return-url';
import type {
  AiChatDisplayMode,
  AuthAccountStatus,
  MasterAccountRole,
  MembershipSource,
  UserRole
} from '@/graphql';
import { GetCurrentUserDocument } from '@/graphql';
import { CombinedGraphQLErrors } from '@apollo/client';
import { GraphQLErrorCode } from '@insio/validation';
import type { Temporal } from '@insio/dates';
import { toPlainDate } from '@insio/dates';
import { identifyUser, resetAnalytics } from '@/services/analytics';
import { setContextRequiredCallback, setUnauthenticatedCallback } from '@/services/apollo-client';
import { isUnauthenticatedError } from '@/services/auth-error';

// ============================================================================
// Types
// ============================================================================

/** A master account the person belongs to, with their roles in it. */
export interface MasterAccountAccess {
  id: string;
  name: string;
  roles: MasterAccountRole[];
}

export interface AuthUser {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  displayName: string;
  roles: UserRole[];
  emailVerified: boolean;
  finishedOnboarding?: boolean;
  /** True when the user dismissed (or completed) the email-setup prompt */
  emailSetupDismissed?: boolean;
  /** True when the user has accepted the current Terms + Privacy Policy */
  policiesAccepted: boolean;
  /** True once the person has decided on GDPR marketing consent, on any version */
  gdprConsentDecided: boolean;
  /** True when the user currently accepts GDPR marketing communications */
  gdprConsentAccepted: boolean;
  /** True when the current session is running under impersonation */
  isImpersonating: boolean;
  /** True when the current account runs the AI chat in simplified mode (no approvals, no greeting) */
  aiChatSimplifiedMode?: boolean;
  /** Registration timestamp — source of the PostHog `signup_date` person property */
  createdAt?: Temporal.Instant;
  /** Server-merged preferences (user override + org default) */
  currentPreferences?: {
    locale?: string | null;
    greetingEnabled?: boolean | null;
    chatDisplayMode?: AiChatDisplayMode | null;
  } | null;
  account?: {
    id: string;
    name: string;
    status?: AuthAccountStatus;
    /** Set while the account is PENDING_DELETION — when the grace period ends it is purged. */
    deletionScheduledAt?: Temporal.Instant | null;
    /** True when this account is a master account's own books ("Moje kancelář"). */
    isMasterAccountOwnBooks?: boolean;
    /** The master account managing this account; null unless it is a master account's client. */
    managingMasterAccount?: { id: string; name: string } | null;
  } | null;
  /** The master account the session acts in — the board; set only when `account` is null. */
  currentMasterAccount?: MasterAccountAccess | null;
  /** Master accounts this person belongs to — the master account half of the account switcher. */
  masterAccounts?: MasterAccountAccess[];
  /** Whether the membership in the current account was granted directly or by a master account. */
  source?: MembershipSource | null;
  /** Master account that granted the current membership, set only when `source` is MASTER_ACCOUNT. */
  grantedByMasterAccount?: { id: string; name: string } | null;
  /** Every account this identity can act in — the source for the account switcher. */
  memberships?: {
    id: string;
    roles: string[];
    status: string;
    source: MembershipSource;
    /** Master account that granted this row, set only when `source` is MASTER_ACCOUNT. */
    grantedByMasterAccount?: { id: string } | null;
    account: {
      id: string;
      name: string;
      /** True when this account is a master account's own books ("Moje kancelář"). */
      isMasterAccountOwnBooks?: boolean;
    };
  }[];
}

export type AuthErrorType = 'unauthenticated' | 'network' | 'unknown' | null;

/** GraphQL codes that refuse one request but leave the session as it was. */
const TRANSIENT_GRAPHQL_CODES: ReadonlySet<unknown> = new Set([
  GraphQLErrorCode.SERVICE_UNAVAILABLE,
  GraphQLErrorCode.RATE_LIMIT_EXCEEDED,
  GraphQLErrorCode.ACCOUNT_CONTEXT_REQUIRED,
  GraphQLErrorCode.MASTER_ACCOUNT_CONTEXT_REQUIRED
]);

/** A transport failure, an HTTP error, or GraphQL errors that are all transient. */
function isNetworkError(error: unknown): boolean {
  if (!CombinedGraphQLErrors.is(error)) return true;
  return error.errors.every(e => TRANSIENT_GRAPHQL_CODES.has(e.extensions?.code));
}

interface AuthContextValue {
  /** Current authenticated user, null if not authenticated */
  user: AuthUser | null;
  /** True while initial auth check is in progress */
  isLoading: boolean;
  /** True if user is authenticated */
  isAuthenticated: boolean;
  /** Error type if authentication failed */
  errorType: AuthErrorType;
  /** Refetch user profile (use after sign-in) */
  refetch: () => Promise<unknown>;
  /** Clear auth state and redirect to sign-in */
  clearAuth: () => Promise<void>;
  /** Get return URL and clear it from storage */
  consumeReturnUrl: () => string;
}

/** Drops the tab's organization-entry decision, which belongs to the person signed in. */
function forgetAccountEntryAcknowledged(): void {
  try {
    sessionStorage.removeItem(ACCOUNT_ENTRY_ACKNOWLEDGED_KEY);
  } catch {
    // Nothing to forget if storage is unavailable.
  }
}

// ============================================================================
// Context
// ============================================================================

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ============================================================================
// Provider
// ============================================================================

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const client = useApolloClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);

  const isCurrentRoutePublic = isPublicRoute(location.pathname);

  const { data, loading, error, refetch } = useQuery(GetCurrentUserDocument, {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true
  });

  // Guard against concurrent/recursive refetch calls triggered by the error link.
  // Without this: getCurrentUser → UNAUTHENTICATED → error link → callback → refetch() → ∞
  const refetchInProgressRef = useRef(false);

  // Register unauthenticated callback — triggers refetch when ANY query returns 401,
  // not just getCurrentUser. This replaces the old SESSION_CHECK_INTERVAL polling.
  useEffect(() => {
    setUnauthenticatedCallback(() => {
      if (refetchInProgressRef.current) return;

      refetchInProgressRef.current = true;
      refetch()
        .catch(() => {
          // Error will surface via errorType detection in the query state
        })
        .finally(() => {
          refetchInProgressRef.current = false;
        });
    });

    return () => {
      setUnauthenticatedCallback(null);
    };
  }, [refetch]);

  // A refusal for the other context re-reads the session and, if the page was drawn for the
  // other context, lands on the home of the one it is in; a page that matches stays put.
  const contextRecoveryRef = useRef(false);
  const pathnameRef = useRef(location.pathname);
  const shownInMasterRef = useRef(false);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);
  useEffect(() => {
    shownInMasterRef.current = !!data?.profile?.currentMasterAccount;
  }, [data]);
  useEffect(() => {
    setContextRequiredCallback(() => {
      if (contextRecoveryRef.current || isPublicRoute(pathnameRef.current)) return;
      contextRecoveryRef.current = true;
      const shownInMaster = shownInMasterRef.current;
      refetch()
        .then(result => {
          const fresh = result.data?.profile;
          if (!fresh || !!fresh.currentMasterAccount === shownInMaster) return;
          const home = fresh.currentMasterAccount
            ? (masterAccountBoardHomePath(masterPermissionCheck(fresh)) ?? navigation.dashboard())
            : navigation.dashboard();
          if (pathnameRef.current !== home) navigate(home, { replace: true });
        })
        .catch(() => {
          // Error will surface via errorType detection in the query state
        })
        .finally(() => {
          contextRecoveryRef.current = false;
        });
    });

    return () => {
      setContextRequiredCallback(null);
    };
  }, [refetch, navigate]);

  const rawProfile = useMemo(
    () => (data?.profile ? { ...data.profile, masterAccounts: data.myMasterAccounts ?? [] } : null),
    [data]
  );

  // A 429, 5xx or dropped connection is 'network'; any other GraphQL error is 'unknown'.
  const errorType = useMemo((): AuthErrorType => {
    if (!error) return null;
    if (isUnauthenticatedError(error)) return 'unauthenticated';
    return !rawProfile && isNetworkError(error) ? 'network' : 'unknown';
  }, [error, rawProfile]);

  // The last profile shown, kept through a 'network' failure so a rate limit signs nobody out.
  const lastProfileRef = useRef<typeof rawProfile>(null);
  const profile =
    errorType === 'unauthenticated'
      ? null
      : (rawProfile ?? (errorType === 'network' ? lastProfileRef.current : null));
  useEffect(() => {
    lastProfileRef.current = profile;
  }, [profile]);

  const user = useMemo<AuthUser | null>(
    () => (profile ? { ...profile, email: profile.email ?? null } : null),
    [profile]
  );
  const isAuthenticated = !!user;

  // A URL saved when this person is forced out stays theirs; see utils/return-url.
  useEffect(() => {
    if (user?.id) rememberLastUser(user.id);
  }, [user?.id]);

  // Keeps the PostHog person in sync; signup_date goes through $set_once and is never overwritten.
  useEffect(() => {
    if (!user?.id) return;
    identifyUser(
      user.id,
      {
        email: user.email ?? undefined,
        name: user.displayName || undefined,
        roles: user.roles
      },
      user.createdAt ? { signup_date: toPlainDate(user.createdAt, 'UTC').toString() } : undefined
    );
  }, [user?.id, user?.email, user?.displayName, user?.roles, user?.createdAt]);

  // Applies the server-merged locale once per person and context (account or master account), so
  // entering another context brings its language while one picked within a context stays.
  const localeSyncKey = user
    ? [user.id, user.account?.id ?? '', user.currentMasterAccount?.id ?? ''].join('|')
    : null;
  const lastLocaleSyncKey = useRef<string | null>(null);
  useEffect(() => {
    if (!localeSyncKey || localeSyncKey === lastLocaleSyncKey.current) return;

    lastLocaleSyncKey.current = localeSyncKey;

    const effectiveLocale = user?.currentPreferences?.locale?.toLowerCase() ?? i18n.language;

    if (effectiveLocale !== i18n.language) {
      i18n.changeLanguage(effectiveLocale);
    }
  }, [localeSyncKey, user?.currentPreferences?.locale, i18n]);

  // Return URL management — only ever handed back to the person it was saved for.
  const userId = user?.id;
  const consumeReturnUrl = useCallback(() => {
    if (!userId) {
      forgetReturnUrl();
      return navigation.dashboard();
    }
    return takeReturnUrl(userId) ?? navigation.dashboard();
  }, [userId]);

  const saveReturnUrl = useCallback((url: string) => {
    if (url && url !== navigation.signIn()) {
      rememberReturnUrl(url);
    }
  }, []);

  // Clear auth state and redirect to sign-in
  const clearAuth = useCallback(async () => {
    // Block the unauthenticated callback from firing a spurious refetch
    // while we intentionally reset the Apollo store during sign-out.
    refetchInProgressRef.current = true;

    resetAnalytics();

    lastProfileRef.current = null;

    try {
      // Use clearStore() instead of resetStore() — resetStore() re-runs active queries which get
      // aborted immediately when navigation unmounts them, producing unhandled AbortErrors.
      await client.clearStore();
    } finally {
      refetchInProgressRef.current = false;
    }
    forgetReturnUrl();
    forgetLastUser();
    // The next person on this tab must get the organization picker and no stranger's board URL.
    forgetAccountEntryAcknowledged();
    forgetMasterAccountBoardUrl();

    // Navigate to sign-in
    navigate(navigation.signIn(), { replace: true });
  }, [client, navigate]);

  // Handle authentication state changes and redirects
  useEffect(() => {
    // Wait for query to complete (either with data or error)
    if (loading) return;

    setHasCheckedAuth(true);

    // The server ended the session: whoever signs in next on this tab picks an organization.
    if (errorType === 'unauthenticated') forgetAccountEntryAcknowledged();

    // If not authenticated and trying to access protected route, redirect to sign-in.
    // A network failure is not a sign-out, so it does not redirect.
    if (!isAuthenticated && !isCurrentRoutePublic && errorType !== 'network') {
      // Store the attempted URL for redirect after login
      saveReturnUrl(location.pathname + location.search);
      navigate(navigation.signIn(), { replace: true });
    }

    // A signed-in person on a public page goes to the saved URL, except the impersonation callback,
    // the policies pages and the e-mail unsubscribe page, which must render for anyone.
    const isImpersonateCallback = location.pathname === navigation.impersonateCallback();
    const isPoliciesPage =
      location.pathname === navigation.policies() ||
      location.pathname === navigation.termsOfService();
    const isUnsubscribePage = location.pathname === navigation.unsubscribe();
    // Password, magic-link and OAuth sign-ins all land here (OAuth returns to the sign-in page).
    if (
      isAuthenticated &&
      isCurrentRoutePublic &&
      !isImpersonateCallback &&
      !isPoliciesPage &&
      !isUnsubscribePage
    ) {
      navigate(consumeReturnUrl(), { replace: true });
    }
  }, [
    data?.profile,
    error,
    isAuthenticated,
    loading,
    isCurrentRoutePublic,
    errorType,
    location.pathname,
    location.search,
    navigate,
    saveReturnUrl,
    consumeReturnUrl
  ]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading: !hasCheckedAuth,
      isAuthenticated,
      errorType,
      refetch: () => refetch(),
      clearAuth,
      consumeReturnUrl
    }),
    [user, hasCheckedAuth, isAuthenticated, errorType, refetch, clearAuth, consumeReturnUrl]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ============================================================================
// Hook
// ============================================================================

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
